/**
 * Data Privacy Mode masking, in the browser (demo aid, not a security control: the real data
 * still reaches the browser). Swaps student names, IDs, dates of birth, addresses and school
 * names for realistic fakes on screen and in downloads.
 *
 * Consistent for the tab's session: a random salt is made when the mode is first used, and
 * school fakes are kept in a registry so the same school always shows the same fake name and a
 * masked school picked in a dropdown can be turned back into the real one for the API.
 */
import { DATA_PRIVACY_PAGES, DATA_PRIVACY_SESSION_KEYS, isDataPrivacyOn } from "./dataPrivacy";
import NAMES from "./privacyNames.json";

type Pools = { weights: Record<string, number>; last: Record<string, string[]>; female: Record<string, string[]>; male: Record<string, string[]> };
const POOLS = NAMES as Pools;
const LOCALES = Object.keys(POOLS.weights);
const FEMALE = new Set(Object.values(POOLS.female).flat().map((n) => n.toLowerCase()));
const MALE = new Set(Object.values(POOLS.male).flat().map((n) => n.toLowerCase()));

const [SALT_KEY, SCHOOLS_KEY] = DATA_PRIVACY_SESSION_KEYS;

// ── Seeded randomness ────────────────────────────────────────────────

const storage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

let salt: string | null = null;
const getSalt = (): string => {
  if (salt) return salt;
  const s = storage();
  salt = s?.getItem(SALT_KEY) || Math.random().toString(36).slice(2) + Date.now().toString(36);
  try {
    s?.setItem(SALT_KEY, salt);
  } catch {
    // Storage blocked: fakes stay consistent until the page reloads.
  }
  return salt;
};

/** cyrb53 string hash. */
const hash = (str: string, seed = 0): number => {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
};

/** mulberry32 generator seeded from (kind, value, attempt). */
const rng = (kind: string, value: string, attempt = 0) => {
  let a = hash(`${getSalt()}\u0000${kind}\u0000${value}\u0000${attempt}`) >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const pickFrom = <T,>(r: () => number, list: T[]): T => list[Math.floor(r() * list.length)];

const nameFromPools = (r: () => number, kind: "last" | "female" | "male" | "any"): string => {
  let roll = r() * 100;
  let locale = LOCALES[0];
  for (const l of LOCALES) {
    roll -= POOLS.weights[l];
    if (roll < 0) {
      locale = l;
      break;
    }
  }
  const k = kind === "any" ? (r() < 0.5 ? "female" : "male") : kind;
  return pickFrom(r, POOLS[k][locale]);
};

const keepCase = (src: string, fake: string): string => {
  const letters = src.replace(/[^A-Za-z]/g, "");
  if (letters && letters === letters.toUpperCase()) return fake.toUpperCase();
  if (letters && letters === letters.toLowerCase()) return fake.toLowerCase();
  return fake;
};

const blank = (v: unknown): v is null | undefined | "" => v === null || v === undefined || String(v).trim() === "";

// ── People ───────────────────────────────────────────────────────────

export const maskLastName = (real: string | null | undefined): string | null | undefined => {
  if (blank(real)) return real;
  const k = String(real).trim().toLowerCase();
  for (let attempt = 0; attempt < 30; attempt++) {
    const fake = nameFromPools(rng("last", k, attempt), "last");
    if (fake.toLowerCase() !== k) return keepCase(String(real), fake);
  }
  return keepCase(String(real), "Smith");
};

/** Keeps the likely gender of the name. */
export const maskFirstName = (real: string | null | undefined): string | null | undefined => {
  if (blank(real)) return real;
  const k = String(real).trim().toLowerCase();
  const kind = FEMALE.has(k) && !MALE.has(k) ? "female" : MALE.has(k) && !FEMALE.has(k) ? "male" : "any";
  for (let attempt = 0; attempt < 30; attempt++) {
    const fake = nameFromPools(rng("first", k, attempt), kind);
    if (fake.toLowerCase() !== k) return keepCase(String(real), fake);
  }
  return keepCase(String(real), "Alex");
};

/** Same shape as the original: digits stay digits, letters letters, a UUID stays a UUID. */
export const maskId = (kind: string, real: string | number | null | undefined): string | null | undefined => {
  if (blank(real)) return real as null | undefined;
  const v = String(real);
  for (let attempt = 0; attempt < 10; attempt++) {
    const r = rng(`id:${kind}`, v, attempt);
    const out = v.replace(/[0-9A-Za-z]/g, (c) => {
      if (/[0-9]/.test(c)) return String(Math.floor(r() * 10));
      if (/[a-f]/.test(c) && /^[0-9a-f-]{36}$/.test(v)) return "0123456789abcdef"[Math.floor(r() * 16)];
      if (/[A-Z]/.test(c)) return "ABCDEFGHJKLMNPQRSTUVWXYZ"[Math.floor(r() * 24)];
      return "abcdefghijkmnpqrstuvwxyz"[Math.floor(r() * 24)];
    });
    if (out !== v) return out;
  }
  return v;
};

/** "123 Main St, Apt 4, Springfield, NC 28202" becomes a fake street, city and ZIP; the state is kept. */
export const maskAddress = (real: string | null | undefined): string | null | undefined => {
  if (blank(real) || ["—", "-"].includes(String(real).trim())) return real;
  const t = String(real).trim();
  const r = rng("address", t.toLowerCase());
  const num = /^(\d+)/.exec(t);
  const digits = num ? Math.max(2, Math.min(5, num[1].length)) : 4;
  const number = String(10 ** (digits - 1) + Math.floor(r() * 9 * 10 ** (digits - 1)));
  const suffixes = ["St", "Ave", "Rd", "Dr", "Ln", "Ct", "Way", "Blvd", "Pl", "Cir", "Trl", "Pkwy"];
  let out = `${number} ${nameFromPools(r, "last")} ${pickFrom(r, suffixes)}, ${pickFrom(r, PLACE_ROOTS)}`;
  const st = /\b([A-Z]{2})\s*,?\s*(\d{5})(?:-\d{4})?\s*$/.exec(t);
  if (st) out += `, ${st[1]} ${st[2][0]}${Array.from({ length: 4 }, () => Math.floor(r() * 10)).join("")}`;
  return out;
};

const US_DATE = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/** Shifted 1 to 25 days, the same way for the same student, so ages still fit the grade. */
export const maskDob = (last: string | null | undefined, first: string | null | undefined, dob: string | null | undefined): string | null | undefined => {
  if (blank(dob)) return dob;
  const t = String(dob).trim();
  const r = rng("dob", `${(last || "").trim().toLowerCase()}|${(first || "").trim().toLowerCase()}|${t}`);
  const shift = (1 + Math.floor(r() * 25)) * (r() < 0.5 ? 1 : -1);
  const us = US_DATE.exec(t);
  const d = us ? new Date(Date.UTC(+us[3], +us[1] - 1, +us[2])) : new Date(`${t.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return maskId("dob", t);
  d.setUTCDate(d.getUTCDate() + shift);
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return us ? `${mm}/${dd}/${d.getUTCFullYear()}` : `${d.getUTCFullYear()}-${mm}-${dd}`;
};

// ── Schools ──────────────────────────────────────────────────────────

export const PLACE_ROOTS: string[] = (() => {
  const roots = [
    "Ashford", "Bayview", "Birchwood", "Briarcliff", "Brookside", "Cedar Ridge", "Clearwater", "Cobblestone", "Crestview",
    "Deer Creek", "Eastwood", "Elm Park", "Fairview", "Fox Hollow", "Glenwood", "Greenfield", "Harbor View", "Hawthorne",
    "Hickory Hill", "Highland Park", "Holly Springs", "Jefferson", "Kingsley", "Lakeside", "Laurel Hill", "Lincoln",
    "Maple Grove", "Meadowbrook", "Mill Creek", "Northgate", "Oak Hollow", "Orchard Park", "Parkside", "Pine Ridge",
    "Pleasant Valley", "Quail Run", "Redwood", "Ridgeview", "River Bend", "Riverside", "Rosemont", "Sandy Hill",
    "Shady Grove", "Silver Lake", "Southfield", "Spring Valley", "Stonebridge", "Summit", "Sunnyside", "Sycamore",
    "Timberline", "Twin Oaks", "Valley View", "Westbrook", "Westfield", "Whispering Pines", "Willow Creek", "Windsor",
    "Woodland", "Woodside", "Aspen Grove", "Beacon Hill", "Bluebird", "Canyon Creek", "Chestnut Hill", "Cypress",
    "Driftwood", "Evergreen", "Foxboro", "Garden Grove", "Hidden Valley", "Juniper", "Liberty", "Magnolia", "Northwood",
    "Prairie View", "Sage Hill", "Sequoia", "Sterling", "Tall Oaks", "Union", "Vista", "Wildwood", "Walnut Hill",
  ];
  const first = ["Oak", "Maple", "Cedar", "Willow", "Pine", "Elm", "Ash", "Birch", "Aspen", "Hazel", "Holly", "Laurel",
    "Briar", "Fox", "Deer", "Hawk", "Eagle", "Wren", "Robin", "Stone", "Brook", "River", "Lake", "Spring", "Sun", "Clear",
    "Green", "Silver", "Gold", "Red", "North", "South", "East", "West", "Glen", "Mill", "Rose", "Fair", "Bright", "Meadow",
    "Hill", "Kings", "Bell", "Rock", "Sand", "Har", "Lin", "Wake", "Ridge"];
  const second = ["wood", "field", "brook", "view", "ridge", "dale", "crest", "haven", "mont", "gate", "side", "vale",
    "ford", "ton", "croft", "grove", "land", "port", "wick", "mere"];
  const all = new Set(roots);
  first.forEach((a) => second.forEach((b) => {
    if (!a.toLowerCase().endsWith(b)) all.add(a + b);
  }));
  return Array.from(all);
})();

/** Values that name no particular school; kept as they are. */
const GENERIC_SCHOOLS = new Set([
  "", "unknown", "other schools", "none", "n/a", "na", "out of county", "out of district", "out of state",
  "private school", "not in school", "charter school", "home school", "homeschool", "home school / virtual school",
  "out-of-county / out-of-state school", "vpk / preschool", "preschool", "virtual school", "all schools (district)",
]);

const TYPE_WORDS = new Set([
  "elementary", "elem", "middle", "high", "school", "schools", "academy", "magnet", "primary", "preschool",
  "intermediate", "prep", "preparatory", "center", "campus", "arts", "stem", "montessori", "alternative", "virtual",
  "early", "college", "of", "excellence", "charter", "international", "lab", "laboratory", "k-8", "6-12", "steam",
  "leadership", "technology", "science", "language", "immersion", "global", "studies",
]);

/** The school's type as a suffix ("Elementary School", "Arts Academy"), the place name dropped. */
export const schoolType = (name: string): string => {
  const lastPart = name.includes(" - ") ? name.slice(name.lastIndexOf(" - ") + 3) : name;
  const tokens = lastPart.trim().split(/\s+/);
  let i = tokens.length;
  while (i > 0 && TYPE_WORDS.has(tokens[i - 1].replace(/[,.()]/g, "").toLowerCase())) i--;
  if (i === tokens.length) return "School";
  const suffix = tokens.slice(i).map((t) => (t === "Elem" ? "Elementary" : t)).join(" ");
  return suffix.toLowerCase().startsWith("of ") ? `School ${suffix}` : suffix;
};

type Registry = { forward: Record<string, string>; reverse: Record<string, string> };
let registry: Registry | null = null;
const loadRegistry = (): Registry => {
  if (registry) return registry;
  try {
    const raw = storage()?.getItem(SCHOOLS_KEY);
    registry = raw ? JSON.parse(raw) : { forward: {}, reverse: {} };
  } catch {
    registry = { forward: {}, reverse: {} };
  }
  return registry!;
};
const saveRegistry = () => {
  try {
    storage()?.setItem(SCHOOLS_KEY, JSON.stringify(loadRegistry()));
  } catch {
    // Storage blocked: the registry lives until the page reloads.
  }
};

/** A school name, consistently and one-to-one for the session; codes without letters are masked as identifiers. */
export const maskSchool = (real: string | null | undefined): string | null | undefined => {
  if (blank(real)) return real;
  const t = String(real).trim();
  if (GENERIC_SCHOOLS.has(t.toLowerCase())) return real;
  if (!/[A-Za-z]/.test(t)) return maskId("school", t);
  const reg = loadRegistry();
  const key = t.toLowerCase();
  if (reg.forward[key]) return reg.forward[key];
  // Already a fake (e.g. a masked dropdown value passing through again): leave it.
  if (reg.reverse[key]) return t;
  const suffix = schoolType(t);
  let fake = "";
  for (let attempt = 0; attempt < 5000; attempt++) {
    const candidate = `${pickFrom(rng("school", key, attempt), PLACE_ROOTS)} ${suffix}`;
    const c = candidate.toLowerCase();
    if (!reg.reverse[c] && !reg.forward[c]) {
      fake = candidate;
      break;
    }
  }
  if (!fake) fake = `School ${maskId("school-fallback", t.replace(/[^A-Za-z0-9]/g, "").toUpperCase())}`;
  reg.forward[key] = fake;
  reg.reverse[fake.toLowerCase()] = t;
  saveRegistry();
  return fake;
};

/** The real school behind a masked name (for filters sent to the API); unknown names pass through. */
export const unmaskSchool = (masked: string | null | undefined): string | null | undefined => {
  if (blank(masked)) return masked;
  return loadRegistry().reverse[String(masked).trim().toLowerCase()] ?? masked;
};

/** Free-text reasons can hold staff names, dates and school abbreviations: mapped to a fixed vocabulary. */
export const maskReason = (real: string | null | undefined): string | null | undefined => {
  if (blank(real)) return real;
  const r = String(real).toLowerCase();
  const rules: [string, string[]][] = [
    ["Duplicate application", ["duplicate"]],
    ["Does not meet eligibility criteria", ["minimum age", "wrong grade", "eligib"]],
    ["Did not participate in screening", ["screening"]],
    ["Renewal student, not new", ["renewal"]],
    ["Student will attend another school", ["assigned elsewhere", "accepted at", "will attend"]],
    ["Family moved", ["moving", "moved"]],
    ["Employee application issue", ["employee"]],
    ["Application withdrawn", ["withdr"]],
    ["Family changed their mind", ["changed", "mind"]],
    ["Parent declined the seat", ["reject", "declin", "email"]],
  ];
  for (const [label, words] of rules) if (words.some((w) => r.includes(w))) return label;
  return "Removed by staff";
};

/** Forgets this session's fakes (called when the mode is switched off). */
export const resetDataPrivacyMasks = (): void => {
  registry = null;
  salt = null;
  try {
    storage()?.removeItem(SCHOOLS_KEY);
    storage()?.removeItem(SALT_KEY);
  } catch {
    // nothing to clear
  }
};

/** True when the mode is on and the current page is one the mode covers. */
export const isMaskedHere = (): boolean =>
  isDataPrivacyOn() && DATA_PRIVACY_PAGES.some((p) => window.location.pathname === p || window.location.pathname.startsWith(`${p}/`));

/** School display name for By School / By Program: masked only on the covered pages. */
export const maskSchoolHere = (name: string | null | undefined): string | null | undefined => (isMaskedHere() ? maskSchool(name) : name);

/** A By School / By Program student detail row (and the enrollment variant's codes). */
export const maskStudentDetail = <T extends Record<string, any>>(r: T): T => {
  const c: Record<string, any> = { ...r };
  if ("studentId" in r) c.studentId = maskId("student", r.studentId);
  if ("studentNumber" in r) c.studentNumber = maskId("student", r.studentNumber);
  if ("firstName" in r) c.firstName = maskFirstName(r.firstName);
  if ("lastName" in r) c.lastName = maskLastName(r.lastName);
  if ("address" in r) c.address = maskAddress(r.address);
  if ("residentSchool" in r) c.residentSchool = maskSchool(r.residentSchool);
  if ("servingSchool" in r) c.servingSchool = maskSchool(r.servingSchool);
  if ("residentDistrict" in r) c.residentDistrict = maskId("district", r.residentDistrict);
  if ("servingDistrict" in r) c.servingDistrict = maskId("district", r.servingDistrict);
  return c as T;
};
